import type { AriaLabelingProps, DOMProps } from '@react-types/shared';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import type defaultTheme from 'tailwindcss/defaultTheme';

import { TObject } from '@infinex-private/ts-utils/object';
import { CircleHelpIcon, InfoIcon } from '@infinex/ui-icons';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';

import { Button } from '../button';
import type { DialogProps, DialogTriggerProps } from '../dialog';
import { Dialog, DialogTrigger } from '../dialog';

export type ContextualHelpProps = {
  /** The content of the popover. */
  children: ReactNode;
  /**
   * Indicates whether popover content is informative or provides helpful guidance.
   *
   * @default 'help'
   */
  variant?: 'info' | 'help';
  /**
   * The size of the contextual help button. Unlike other component T-shirt sizes,
   * this is based on Tailwind’s typography scale.
   *
   * @default 'base'
   */
  size?: ContextualHelpSize;
} & Pick<DialogTriggerProps, 'defaultOpen' | 'isOpen' | 'onOpenChange'> &
  Pick<DialogProps, 'placement' | 'mobileType'> &
  DOMProps &
  AriaLabelingProps &
  BaseStyleProps &
  UnsafeStyleProps;

/**
 * Contextual help shows a user extra information about the state or behaviour
 * of either an adjacent component or the related view.
 *
 * [Taurus docs: `ContextualHelp`](https://ui-sb.app.infinex.dev/?path=/docs/components-contextualhelp--docs)
 */
export const ContextualHelp = forwardRef(function ContextualHelp(
  props: ContextualHelpProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const {
    children,
    defaultOpen,
    isOpen,
    mobileType,
    onOpenChange,
    placement = 'bottom start',
    size = 'base',
    variant,
    ...buttonProps
  } = props;

  const Icon = variant === 'info' ? InfoIcon : CircleHelpIcon;
  const iconSize = getIconSize(size);
  const label = variant === 'info' ? 'Information' : 'Help';
  const ariaLabel = 'aria-label' in props ? props['aria-label'] : undefined;

  return (
    <DialogTrigger
      defaultOpen={defaultOpen}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Button
        ref={ref}
        aria-label={label}
        prominence="low"
        {...buttonProps}
        className={cn(
          'text-default min-h-0 min-w-0',
          'hover:text-emphasis',
          getButtonSizeClasses(size),
          buttonProps.className
        )}
      >
        <Icon style={{ height: iconSize, width: iconSize }} />
      </Button>
      <Dialog
        aria-label={ariaLabel}
        className="md:min-w-64"
        placement={placement}
        mobileType={mobileType}
        type="popover"
      >
        {children}
      </Dialog>
    </DialogTrigger>
  );
});

// Helpers --------------------------------------------------------------------

type FontSize = keyof typeof defaultTheme.fontSize;
const iconSizes = {
  xs: 14,
  sm: 16,
  base: 18,
  lg: 20,
} satisfies Partial<Record<FontSize, number>>;

/** @private Exported for documentation only. */
export const CONTEXTUAL_HELP_SIZES = TObject.keys(iconSizes);
/** @private Exported for documentation only. */
export type ContextualHelpSize = keyof typeof iconSizes;

/**
 * Special pixel sizes for the contextual help button and icon. Deviates from
 * typical T-shirt sizes to align with typography scale.
 */
function getButtonSizeClasses(size: ContextualHelpSize) {
  return {
    'size-4 rounded-2xsmall': size === 'xs',
    'size-5 rounded-2xsmall': size === 'sm',
    'size-6 rounded-xsmall': size === 'base',
    'size-7 rounded-xsmall': size === 'lg',
  };
}
function getIconSize(size: ContextualHelpSize) {
  return iconSizes[size];
}
